// useDecorations Hook
// Manages decoration equipment state and operations

import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { decorationsAPI, type EquipDecorationRequest, type UnequipDecorationRequest } from '../api/decorations';
import { toast } from 'react-hot-toast';

export function useEquippedDecorations() {
  return useQuery({
    queryKey: ['decorations', 'equipped'],
    queryFn: async () => {
      const response = await decorationsAPI.getEquipped();
      return response.data.equipped;
    },
    staleTime: 5 * 60 * 1000, // 5 minutes
    gcTime: 10 * 60 * 1000 // 10 minutes
  });
}

export function useEquipDecoration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: EquipDecorationRequest) => decorationsAPI.equip(data),
    onSuccess: (response) => {
      // Invalidate equipped decorations query
      queryClient.invalidateQueries({ queryKey: ['decorations', 'equipped'] });
      
      // Show success message
      toast.success(response.message || 'Decoration equipped successfully');
    },
    onError: (error: any) => {
      // Show error message
      const message = error.response?.data?.error || error.message || 'Failed to equip decoration';
      toast.error(message);
    }
  });
}

export function useUnequipDecoration() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: UnequipDecorationRequest) => decorationsAPI.unequip(data),
    onSuccess: (response) => {
      // Invalidate equipped decorations query
      queryClient.invalidateQueries({ queryKey: ['decorations', 'equipped'] });
      
      // Show success message
      toast.success(response.message || 'Decoration unequipped successfully');
    },
    onError: (error: any) => {
      // Show error message
      const message = error.response?.data?.error || error.message || 'Failed to unequip decoration';
      toast.error(message);
    }
  });
}
